import { Calendar, Inbox, AlertTriangle, Video, MapPin } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { Skeleton } from './Skeleton';
import { cn } from '../lib/cn';
import type {
  CalendarTodayWidget,
  InboxSummaryWidget,
  SlaTodayWidget,
} from '../lib/types';

// Shared card chrome. Widgets are small dashboard tiles that render above
// the announcement banner on Home. Each one fetches independently so a
// single failure doesn't blank out the row.

type IconTint = 'navy' | 'orange' | 'healthy' | 'warning' | 'critical';

// Static map so Tailwind's JIT picks up every class — DO NOT build with
// string concatenation. Same for the bar tone classes below.
const ICON_TINTS: Record<IconTint, string> = {
  navy: 'bg-brand-navy/10 text-brand-navy group-hover:bg-brand-navy/15',
  orange: 'bg-brand-orange/15 text-brand-orange group-hover:bg-brand-orange/20',
  healthy: 'bg-status-healthy/15 text-status-healthy group-hover:bg-status-healthy/20',
  warning: 'bg-status-warning/15 text-status-warning group-hover:bg-status-warning/20',
  critical: 'bg-status-critical/15 text-status-critical group-hover:bg-status-critical/20',
};

const Card = ({
  title,
  icon: Icon,
  iconTint = 'navy',
  soon,
  children,
}: {
  title: string;
  icon: typeof Inbox;
  iconTint?: IconTint;
  soon?: boolean;
  children: React.ReactNode;
}) => (
  <div
    className={cn(
      'group rounded-xl border border-line bg-bg-primary p-3.5',
      'transition-all duration-200 ease-out',
      'hover:-translate-y-0.5 hover:border-brand-navy/30 hover:shadow-md',
      soon && 'opacity-90 hover:translate-y-0 hover:shadow-sm',
    )}
  >
    <div className="mb-3 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
            ICON_TINTS[iconTint],
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" strokeWidth={2.25} />
        </div>
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">{title}</h3>
      </div>
      {soon ? (
        <span className="rounded-full bg-brand-orange/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-orange">
          Soon
        </span>
      ) : null}
    </div>
    {children}
  </div>
);

const ComingSoonBody = ({ subtitle }: { subtitle: string }) => (
  <div className="space-y-2">
    <div className="text-sm text-text-secondary">{subtitle}</div>
    <div className="text-xs text-text-tertiary">Available once we wire it up — stay tuned.</div>
  </div>
);

const Unavailable = ({ message }: { message: string }) => (
  <div className="py-1 text-sm text-text-tertiary">{message}</div>
);

// ─── SLA today ──────────────────────────────────────────────────────────────

export const SlaTodayCard = () => {
  const { data, loading, error } = useApi<SlaTodayWidget>('/api/me/widgets/sla-today');

  // Header icon tint reflects the user's current state — calm when clean,
  // amber when at-risk surfaces, red when something's already breached.
  const tint: IconTint =
    !data || !data.tracked
      ? 'navy'
      : data.breached_count > 0
        ? 'critical'
        : data.at_risk_count > 0
          ? 'warning'
          : 'healthy';

  return (
    <Card title="My SLA today" icon={AlertTriangle} iconTint={tint}>
      {loading ? (
        <Skeleton className="h-14 w-full" />
      ) : error || !data ? (
        <Unavailable message="Couldn't load — try refreshing." />
      ) : !data.tracked ? (
        <Unavailable message="You're not tracked in the SLA system yet." />
      ) : (
        <div className="space-y-2.5">
          <div className="flex items-baseline gap-5">
            <Stat label="Pending" value={data.pending_count} />
            <Stat label="At risk" value={data.at_risk_count} tone="warning" />
            <Stat label="Breached" value={data.breached_count} tone="critical" />
          </div>
          <div className="space-y-1 border-t border-line pt-2.5">
            <CompliancePctRow label="Today" pct={data.compliance_pct_today} />
            <CompliancePctRow label="Week" pct={data.compliance_pct_week} />
          </div>
        </div>
      )}
    </Card>
  );
};

// Fill color reflects health: green when ≥90%, amber 70–89%, red below.
const BAR_TONES = {
  healthy: 'bg-status-healthy',
  warning: 'bg-status-warning',
  critical: 'bg-status-critical',
} as const;

const CompliancePctRow = ({ label, pct }: { label: string; pct: number }) => {
  const tone: keyof typeof BAR_TONES =
    pct >= 90 ? 'healthy' : pct >= 70 ? 'warning' : 'critical';
  const width = Math.max(0, Math.min(100, pct));
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-10 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
        {label}
      </span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-bg-secondary">
        <div
          className={cn('h-full rounded-full transition-all duration-500', BAR_TONES[tone])}
          style={{ width: `${width}%` }}
        />
      </div>
      <span className="w-10 text-right text-[11px] font-semibold tabular-nums text-text-secondary">
        {pct.toFixed(0)}%
      </span>
    </div>
  );
};

const Stat = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'warning' | 'critical';
}) => (
  <div className="leading-none">
    <div
      className={cn(
        'text-2xl sm:text-3xl font-semibold tabular-nums text-text-primary',
        tone === 'warning' && value > 0 && 'text-status-warning',
        tone === 'critical' && value > 0 && 'text-status-critical',
      )}
    >
      {value}
    </div>
    <div className="mt-1.5 text-[10px] uppercase tracking-wider text-text-tertiary">{label}</div>
  </div>
);

// ─── Inbox summary ──────────────────────────────────────────────────────────

export const InboxSummaryCard = () => {
  const { data, loading, error } = useApi<InboxSummaryWidget>('/api/me/widgets/inbox-summary');

  return (
    <Card title="Inbox" icon={Inbox} iconTint="navy">
      {loading ? (
        <Skeleton className="h-14 w-full" />
      ) : error || !data ? (
        <Unavailable message="Couldn't load — try refreshing." />
      ) : data.note === 'mailbox_inaccessible' ? (
        <Unavailable message="Mailbox not accessible." />
      ) : (
        <div className="space-y-2.5">
          <div className="flex items-baseline gap-2 leading-none">
            <span className="text-2xl sm:text-3xl font-semibold tabular-nums text-text-primary">
              {data.unread_count}
            </span>
            <span className="text-xs text-text-tertiary">unread</span>
          </div>
          {data.top_senders_today.length > 0 ? (
            <div className="border-t border-line pt-2">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                Top senders today
              </div>
              <ul className="space-y-1 text-[13px]">
                {data.top_senders_today.slice(0, 3).map((s) => (
                  <li
                    key={s.email}
                    className="flex items-center justify-between gap-2 text-text-secondary"
                  >
                    <span className="truncate">{s.name}</span>
                    <span className="tabular-nums text-text-tertiary">{s.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="border-t border-line pt-2.5 text-xs text-text-tertiary">
              No new senders today.
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// ─── Calendar today ─────────────────────────────────────────────────────────

const formatEventTime = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

export const CalendarTodayCard = () => {
  const { data, loading, error } = useApi<CalendarTodayWidget>('/api/me/widgets/calendar-today');

  return (
    <Card title="Today" icon={Calendar} iconTint="orange">
      {loading ? (
        <Skeleton className="h-14 w-full" />
      ) : error || !data ? (
        <Unavailable message="Couldn't load — try refreshing." />
      ) : data.events.length === 0 ? (
        <div className="py-1 text-sm text-text-secondary">
          Nothing on today — enjoy the quiet.
        </div>
      ) : (
        <ul className="-mx-2 space-y-0.5">
          {data.events.slice(0, 3).map((e, i) => {
            const inner = (
              <>
                <div className="text-[11px] font-medium tabular-nums uppercase tracking-wider text-text-tertiary">
                  {formatEventTime(e.start)}–{formatEventTime(e.end)}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-sm text-text-primary">
                  <span className="truncate">{e.subject}</span>
                  {e.is_online ? (
                    <Video className="h-3 w-3 shrink-0 text-text-tertiary" aria-label="Online" />
                  ) : e.location ? (
                    <MapPin className="h-3 w-3 shrink-0 text-text-tertiary" aria-label="In person" />
                  ) : null}
                </div>
              </>
            );
            return (
              <li key={i}>
                {e.join_url ? (
                  <a
                    href={e.join_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block min-h-11 rounded-md px-2 py-1.5 transition-colors hover:bg-bg-secondary active:bg-bg-tertiary"
                  >
                    {inner}
                  </a>
                ) : (
                  <div className="min-h-11 px-2 py-1.5">{inner}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

// ─── Coming-soon variant ────────────────────────────────────────────────────
// Driven by VITE_WIDGETS_PREVIEW=true. Renders the same card shells with a
// "Soon" pill and a one-liner teaser, instead of fetching data. Flip the
// env var to false (or remove it) once email-tracker is wired and the live
// widgets above are ready to surface real data.

const PreviewWidgets = () => (
  <div
    className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3"
    data-tour="widgets-row"
  >
    <div data-tour="widget-today">
      <Card title="Today" icon={Calendar} iconTint="orange" soon>
        <ComingSoonBody subtitle="Your next meetings, all in one place." />
      </Card>
    </div>
    <div data-tour="widget-inbox">
      <Card title="Inbox" icon={Inbox} iconTint="navy" soon>
        <ComingSoonBody subtitle="Unread mail and top senders, at a glance." />
      </Card>
    </div>
    <div data-tour="widget-sla">
      <Card title="My SLA today" icon={AlertTriangle} iconTint="healthy" soon>
        <ComingSoonBody subtitle="Pending, at-risk and breached emails." />
      </Card>
    </div>
  </div>
);

// ─── Public ─────────────────────────────────────────────────────────────────

// Default: preview mode ON unless the flag is explicitly set to "false".
// This makes the safe state ("Coming Soon" cards) the default — a missing
// env var on prod can't accidentally surface stub data as if it were real.
const previewMode = import.meta.env.VITE_WIDGETS_PREVIEW !== 'false';

export const HomeWidgets = () => (
  <section aria-label="At a glance">
    {previewMode ? (
      <PreviewWidgets />
    ) : (
      <div
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3"
        data-tour="widgets-row"
      >
        <div data-tour="widget-today">
          <CalendarTodayCard />
        </div>
        <div data-tour="widget-inbox">
          <InboxSummaryCard />
        </div>
        <div data-tour="widget-sla">
          <SlaTodayCard />
        </div>
      </div>
    )}
  </section>
);
